// 絵 (img.src) と音・JSON (fetch) の読み込みを src/asset_queue.ts の順番に通す (11-02)。
//   main.ts の一番最初に読み込む (ほかの部品が読み込みと同時に頼む絵より先に、ここを差し込むため)。
//   並んでいる間の絵は「まだ読めていない絵」と同じに見せる (complete=false・naturalWidth=0)。
//   11-03 作者の答え「入れる」で配布版の既定。前の読み方 (頼まれた順に全部一斉に読む) は開発版の ?loadOrder=old。
import { AssetQueue, isQueueableAsset } from './asset_queue'

export const assetQueue = new AssetQueue()

function install(): void {
  if (typeof window === 'undefined' || typeof HTMLImageElement === 'undefined') return
  const qs = new URLSearchParams(window.location.search)
  if (import.meta.env.DEV && qs.get('loadOrder') === 'old') return
  // 先のボスの物を番まで読まない (11-03・src/asset_queue.ts holdFar)。開発版の ?bossFx=startup で前の版 (空いたら読む)。
  if (import.meta.env.DEV && qs.get('bossFx') === 'startup') assetQueue.holdFar = false
  const proto = HTMLImageElement.prototype
  const srcDesc = Object.getOwnPropertyDescriptor(proto, 'src')
  const completeDesc = Object.getOwnPropertyDescriptor(proto, 'complete')
  if (!srcDesc?.set || !srcDesc.get || !completeDesc?.get) return
  const setSrc = srcDesc.set, getSrc = srcDesc.get, getComplete = completeDesc.get
  // 並んでいる絵 → { 頼まれた URL・取り消し }
  const pending = new WeakMap<HTMLImageElement, { url: string; cancel: () => void }>()

  Object.defineProperty(proto, 'src', {
    configurable: true, enumerable: srcDesc.enumerable,
    get(this: HTMLImageElement) {
      const p = pending.get(this)
      return p ? new URL(p.url, document.baseURI).href : getSrc.call(this)
    },
    set(this: HTMLImageElement, value: string) {
      const url = String(value)
      const prev = pending.get(this)
      if (prev) { if (prev.url === url) return; prev.cancel(); pending.delete(this) }
      if (!isQueueableAsset(url)) { setSrc.call(this, url); return }
      const img = this
      let started = false
      const cancel = assetQueue.request(url, (done) => {
        started = true
        pending.delete(img)
        img.addEventListener('load', done, { once: true })
        img.addEventListener('error', done, { once: true })
        setSrc.call(img, url)
        if (getComplete.call(img)) done()   // 手元に残っていた時は load を待たない
      })
      if (!started) pending.set(img, { url, cancel })
    },
  })
  Object.defineProperty(proto, 'complete', {
    configurable: true, enumerable: completeDesc.enumerable,
    get(this: HTMLImageElement) { return pending.has(this) ? false : getComplete.call(this) },
  })
  // 並んでいる間に decode() を呼ばれたら、読み始めてから元の decode に渡す。
  const decode = proto.decode
  proto.decode = function (this: HTMLImageElement) {
    if (!pending.has(this)) return decode.call(this)
    return new Promise<void>((resolve, reject) => {
      this.addEventListener('load', () => decode.call(this).then(resolve, reject), { once: true })
      this.addEventListener('error', () => reject(new DOMException('image load failed', 'EncodingError')), { once: true })
    })
  }

  const fetchOrig = window.fetch.bind(window)
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
    if (method !== 'GET' || !isQueueableAsset(url)) return fetchOrig(input, init)
    return new Promise<Response>((resolve, reject) => {
      assetQueue.request(url, (done) => {
        // 本体を読み終えるまで枠を使う (返事の頭だけで数えると、流れている本数を少なく見積もるため)。
        fetchOrig(input, init).then(async (res) => {
          const body = await res.arrayBuffer()
          done()
          resolve(new Response(body, { status: res.status, statusText: res.statusText, headers: res.headers }))
        }).catch((e) => { done(); reject(e) })
      })
    })
  }
  // 覗き窓は Steam 版では出さない (開発用の口を閉じる・11-02)。
  if (import.meta.env.MODE !== 'steam') (window as unknown as { __assetQueue?: AssetQueue }).__assetQueue = assetQueue
}

install()
